import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { FiUsers, FiCheck, FiX, FiShield, FiKey, FiSearch, FiTrash2 } from 'react-icons/fi';
import { format } from 'date-fns';
import api from '../../services/api';
import './Admin.css';
import PasswordRequirements from '../../components/PasswordRequirements/PasswordRequirements';

const Admin = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('approvals');
  const [pendingMembers, setPendingMembers] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Reset password modal state
  const [resetModal, setResetModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');

  const fetchPendingMembers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/pending-approvals');
      if (response.data.success) {
        const members = Array.isArray(response.data.data) 
          ? response.data.data 
          : (response.data.data.items || []);
        setPendingMembers(members);
      }
    } catch (err) {
      console.error('Failed to fetch pending members:', err);
      setError('Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAllMembers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/members');
      if (response.data.success) {
        setAllMembers(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch members:', err);
      setError('Failed to load members');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'approvals') {
      fetchPendingMembers();
    } else {
      fetchAllMembers();
    }
  }, [activeTab, fetchPendingMembers, fetchAllMembers]);

  const handleApprove = async (memberId) => {
    try {
      setActionLoading(memberId);
      const response = await api.post(`/admin/approve/${memberId}`);
      if (response.data.success) {
        setPendingMembers(prev => prev.filter(m => m.memberID !== memberId));
      }
    } catch (err) {
      console.error('Failed to approve member:', err);
      alert('Failed to approve member. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (memberId) => {
    if (!window.confirm('Are you sure you want to reject this registration? This will delete their account.')) {
      return;
    }

    try {
      setActionLoading(memberId);
      const response = await api.post(`/admin/reject/${memberId}`);
      if (response.data.success) {
        setPendingMembers(prev => prev.filter(m => m.memberID !== memberId));
      }
    } catch (err) {
      console.error('Failed to reject member:', err);
      alert('Failed to reject member. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword.trim() || newPassword.length < 6) {
      setResetMessage('Password must be at least 6 characters.');
      return;
    }

    try {
      setResetLoading(true);
      setResetMessage('');
      const response = await api.post(`/admin/reset-password/${resetModal.memberID}`, {
        newPassword: newPassword
      });
      if (response.data.success) {
        setResetMessage(response.data.message);
        setNewPassword('');
        setTimeout(() => {
          setResetModal(null);
          setResetMessage('');
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to reset password:', err);
      setResetMessage(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleDeleteMember = async (member) => {
    if (!window.confirm(`Deactivate ${member.firstName} ${member.lastName}? They will no longer be able to log in.`)) return;
    try {
      const response = await api.delete(`/admin/members/${member.memberID}`);
      if (response.data.success) {
        setAllMembers(prev => prev.filter(m => m.memberID !== member.memberID));
      }
    } catch (err) {
      console.error('Failed to delete member:', err);
      alert(err.response?.data?.message || 'Failed to delete member.');
    }
  };

  const handleRestoreMember = async (member) => {
    if (!window.confirm(`Restore ${member.firstName} ${member.lastName}? They will be able to log in again.`)) return;
    try {
      const response = await api.post(`/admin/members/${member.memberID}/restore`);
      if (response.data.success) {
        setAllMembers(prev => prev.map(m => 
          m.memberID === member.memberID ? { ...m, isActive: true } : m
        ));
      }
    } catch (err) {
      console.error('Failed to restore member:', err);
      alert(err.response?.data?.message || 'Failed to restore member.');
    }
  };

  const filteredMembers = allMembers.filter(m => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      m.firstName.toLowerCase().includes(query) ||
      m.lastName.toLowerCase().includes(query) ||
      m.email.toLowerCase().includes(query)
    );
  });

  const activeMembers = filteredMembers.filter(m => m.isActive);
  const deactivatedMembers = filteredMembers.filter(m => !m.isActive);

  if (!user?.isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="admin-page">
      <div className="container">
        <div className="page-header">
          <h1><FiShield className="page-header-icon" /> Admin Panel</h1>
          <p className="page-subtitle">Manage registrations and members</p>
        </div>

        {/* Tabs */}
        <div className="admin-tabs">
          <button
            className={`admin-tab ${activeTab === 'approvals' ? 'active' : ''}`}
            onClick={() => setActiveTab('approvals')}
          >
            <FiUsers /> Pending Approvals
            {pendingMembers.length > 0 && (
              <span className="admin-tab-badge">{pendingMembers.length}</span>
            )}
          </button>
          <button
            className={`admin-tab ${activeTab === 'members' ? 'active' : ''}`}
            onClick={() => setActiveTab('members')}
          >
            <FiKey /> Manage Members
          </button>
        </div>

        {/* Pending Approvals Tab */}
        {activeTab === 'approvals' && (
          <div className="admin-section">
            {loading ? (
              <div className="loading-state">
                <span className="spinner spinner-lg"></span>
                <p>Loading pending approvals...</p>
              </div>
            ) : error ? (
              <div className="error-state card">
                <p>{error}</p>
                <button className="btn btn-primary" onClick={fetchPendingMembers}>Try Again</button>
              </div>
            ) : pendingMembers.length === 0 ? (
              <div className="empty-state card">
                <div className="empty-state-icon">✅</div>
                <h3 className="empty-state-title">All caught up!</h3>
                <p>No pending registrations to review.</p>
              </div>
            ) : (
              <div className="pending-list">
                {pendingMembers.map(member => (
                  <div key={member.memberID} className="pending-card card">
                    <div className="pending-info">
                      <h3 className="pending-name">
                        {member.firstName} {member.lastName}
                        {member.isManagedProfile && <span className="admin-badge" style={{ background: '#8e6cc0' }}>Managed Profile</span>}
                      </h3>
                      {member.isManagedProfile ? (
                        <p className="pending-email">Created by {member.managedByName}</p>
                      ) : (
                        <p className="pending-email">{member.email}</p>
                      )}
                      {member.createdDate && (
                        <p className="pending-date">
                          Registered: {format(new Date(member.createdDate + 'Z'), 'MMM d, yyyy h:mm a')}
                        </p>
                      )}
                    </div>
                    <div className="pending-actions">
                      <button 
                        className="btn btn-success"
                        onClick={() => handleApprove(member.memberID)}
                        disabled={actionLoading === member.memberID}
                      >
                        {actionLoading === member.memberID ? (
                          <span className="spinner spinner-sm"></span>
                        ) : (
                          <><FiCheck /> Approve</>
                        )}
                      </button>
                      <button 
                        className="btn btn-danger"
                        onClick={() => handleReject(member.memberID)}
                        disabled={actionLoading === member.memberID}
                      >
                        <FiX /> Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Manage Members Tab */}
        {activeTab === 'members' && (
          <div className="admin-section">
            <div className="admin-search">
              <div className="search-input-wrapper">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  className="form-input"
                  placeholder="          Search members..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {loading ? (
              <div className="loading-state">
                <span className="spinner spinner-lg"></span>
                <p>Loading members...</p>
              </div>
            ) : (
<div className="members-admin-list">
                {activeMembers.map(member => (
                  <div key={member.memberID} className="member-admin-card card">
                    <div className="member-admin-info">
                      <h3 className="member-admin-name">
                        {member.firstName} {member.lastName}
                        <span className="member-id-badge">ID: {member.memberID}</span>
                        {member.isAdmin && <span className="admin-badge">Admin</span>}
                      </h3>
                      <p className="member-admin-email">{member.email}</p>
                      <p className="member-admin-meta">
                        Joined: {format(new Date(member.createdDate + 'Z'), 'MMM d, yyyy')}
                        {member.lastLoginDate && (
                          <> · Last login: {format(new Date(member.lastLoginDate + 'Z'), 'MMM d, yyyy h:mm a')}</>
                        )}
                      </p>
                    </div>
                    <div className="member-admin-actions">
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => { setResetModal(member); setNewPassword(''); setResetMessage(''); }}
                      >
                        <FiKey /> Reset Password
                      </button>
                      {!member.isAdmin && (
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeleteMember(member)}
                        >
                          <FiTrash2 /> Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {deactivatedMembers.length > 0 && (
                  <>
                    <h3 className="deactivated-heading">Deactivated Members</h3>
                    {deactivatedMembers.map(member => (
                      <div key={member.memberID} className="member-admin-card card deactivated">
                        <div className="member-admin-info">
                          <h3 className="member-admin-name">
                            {member.firstName} {member.lastName}
                            <span className="member-id-badge">ID: {member.memberID}</span>
                            <span className="deactivated-badge">Deactivated</span>
                          </h3>
                          <p className="member-admin-email">{member.email}</p>
                          <p className="member-admin-meta">
                            Joined: {format(new Date(member.createdDate + 'Z'), 'MMM d, yyyy')}
                          </p>
                        </div>
                        <div className="member-admin-actions">
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => handleRestoreMember(member)}
                          >
                            <FiCheck /> Restore
                          </button>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Reset Password Modal */}
      {resetModal && (
        <div className="modal-overlay" onClick={() => setResetModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reset Password</h3>
              <button className="btn btn-ghost" onClick={() => setResetModal(null)}><FiX /></button>
            </div>
            <div className="modal-body">
              <p>Resetting password for <strong>{resetModal.firstName} {resetModal.lastName}</strong></p>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter new password..."
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoFocus
                />
                <PasswordRequirements password={newPassword} />
              </div>
              {resetMessage && (
                <p className={`reset-message ${resetMessage.includes('Failed') ? 'error' : 'success'}`}>
                  {resetMessage}
                </p>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setResetModal(null)}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleResetPassword}
                disabled={resetLoading || !newPassword.trim()}
              >
                {resetLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;